import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Camera, Video, X, Check, ShieldCheck, Loader2, Send, Plus, Trash2, Building2, Users } from "lucide-react";
import {
  getFormularioPublico, guardarFormularioPublico, completarFormularioPublico,
  subirFotoFormulario, borrarFotoFormulario, subirVideoChunk, borrarVideoFormulario, fotoUrl,
} from "@/lib/prevelis";

const RESP = [
  { v: "si", l: "Cumple", on: "bg-emerald-600 text-white border-emerald-600" },
  { v: "no", l: "No cumple", on: "bg-red-600 text-white border-red-600" },
  { v: "na", l: "No aplica", on: "bg-slate-500 text-white border-slate-500" },
  { v: "nsb", l: "No sé", on: "bg-amber-500 text-white border-amber-500" },
];
const RESP_INFO = [
  { v: "si", l: "Sí", on: "bg-blue-600 text-white border-blue-600" },
  { v: "no", l: "No", on: "bg-slate-600 text-white border-slate-600" },
  { v: "na", l: "No aplica", on: "bg-slate-500 text-white border-slate-500" },
  { v: "nsb", l: "No sé", on: "bg-amber-500 text-white border-amber-500" },
];
const EMP_FIELDS = [["nombre", "Nombre de la empresa"], ["direccion", "Dirección"], ["centro", "Centro de trabajo"], ["cif", "CIF"], ["cnae", "CNAE"], ["telefono", "Teléfono"], ["email", "Email"], ["web", "Web"]];
const CHUNK = 4 * 1024 * 1024;

export default function FormularioPublico() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [respuestas, setRespuestas] = useState({});
  const [bloquesNA, setBloquesNA] = useState([]);
  const [empresa, setEmpresa] = useState({});
  const [trabajadores, setTrabajadores] = useState([]);
  const metaTimer = useRef(null);
  const [abiertos, setAbiertos] = useState({});
  const [saving, setSaving] = useState(false);
  const [completado, setCompletado] = useState(false);
  const [videoPct, setVideoPct] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    getFormularioPublico(token).then((d) => {
      setData(d);
      setRespuestas(d.formulario.respuestas || {});
      setBloquesNA(d.formulario.bloques_na || []);
      setEmpresa(d.formulario.empresa || {});
      setTrabajadores(d.formulario.trabajadores || []);
      setAbiertos({ [d.bloques[0]?.orden]: true });
      setCompletado(d.formulario.estado === "completado" || d.formulario.estado === "revisado");
      setLoading(false);
    }).catch(() => { setLoading(false); toast.error("Formulario no encontrado o no disponible."); });
  }, [token]);

  const autosave = useCallback((next, na) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try { await guardarFormularioPublico(token, { respuestas: next, bloques_na: na }); }
      catch (e) { console.error("Autosave del formulario falló (se reintentará):", e); }
      finally { setSaving(false); }
    }, 900);
  }, [token]);

  const setResp = (cid, patch) => {
    const next = { ...respuestas, [cid]: { ...(respuestas[cid] || {}), ...patch } };
    setRespuestas(next);
    autosave({ [cid]: next[cid] }, bloquesNA);
  };

  const autosaveMeta = useCallback((emp, trabs) => {
    if (metaTimer.current) clearTimeout(metaTimer.current);
    metaTimer.current = setTimeout(async () => {
      setSaving(true);
      try { await guardarFormularioPublico(token, { empresa: emp, trabajadores: trabs }); }
      catch (e) { console.error("Autosave datos empresa/trabajadores (se reintentará):", e); }
      finally { setSaving(false); }
    }, 900);
  }, [token]);
  const setEmp = (k, val) => { const next = { ...empresa, [k]: val }; setEmpresa(next); autosaveMeta(next, trabajadores); };
  const addTrab = () => { const next = [...trabajadores, { id: Math.random().toString(36).slice(2), nombre: "", apellidos: "", dni: "", puesto: "", tareas: "" }]; setTrabajadores(next); autosaveMeta(empresa, next); };
  const setTrab = (id, k, val) => { const next = trabajadores.map((t) => (t.id === id ? { ...t, [k]: val } : t)); setTrabajadores(next); autosaveMeta(empresa, next); };
  const delTrab = (id) => { const next = trabajadores.filter((t) => t.id !== id); setTrabajadores(next); autosaveMeta(empresa, next); };

  const toggleNA = (orden) => {
    const na = bloquesNA.includes(orden) ? bloquesNA.filter((o) => o !== orden) : [...bloquesNA, orden];
    setBloquesNA(na);
    autosave({}, na);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-center text-slate-500">Este formulario no está disponible.</div>;

  const bloques = data.bloques;
  const total = data.total_preguntas;
  const answered = Object.values(respuestas).filter((r) => r?.respuesta).length
    + bloques.filter((b) => bloquesNA.includes(b.orden)).reduce((s, b) => s + b.preguntas.filter((q) => !respuestas[q.checklist_id]?.respuesta).length, 0);
  const pct = Math.min(100, Math.round((answered / total) * 100));

  const onFoto = async (orden, file) => {
    try {
      const foto = await subirFotoFormulario(token, file, orden);
      setData((d) => ({ ...d, formulario: { ...d.formulario, fotos: [...(d.formulario.fotos || []), foto] } }));
    } catch { toast.error("No se pudo subir la fotografía"); }
  };
  const delFoto = async (fid) => {
    try { await borrarFotoFormulario(token, fid); } catch (e) { console.error("Error al borrar foto (best-effort):", e); }
    setData((d) => ({ ...d, formulario: { ...d.formulario, fotos: (d.formulario.fotos || []).filter((f) => f.id !== fid) } }));
  };
  const onVideo = async (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["mp4", "mov"].includes(ext)) return toast.error("Solo se admiten vídeos MP4 o MOV");
    if (file.size > data.contexto.video_max_mb * 1024 * 1024) return toast.error(`El vídeo supera ${data.contexto.video_max_mb} MB`);
    const totalChunks = Math.ceil(file.size / CHUNK);
    setVideoPct(0);
    try {
      let res;
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        res = await subirVideoChunk(token, blob, i, totalChunks, file.name);
        setVideoPct(Math.round(((i + 1) / totalChunks) * 100));
      }
      setData((d) => ({ ...d, formulario: { ...d.formulario, video: res.video } }));
      toast.success("Vídeo subido correctamente");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir el vídeo"); }
    finally { setVideoPct(null); }
  };
  const delVideo = async () => {
    try { await borrarVideoFormulario(token); } catch (e) { console.error("Error al borrar vídeo (best-effort):", e); }
    setData((d) => ({ ...d, formulario: { ...d.formulario, video: null } }));
  };

  const finalizar = async () => {
    try { await guardarFormularioPublico(token, { respuestas, bloques_na: bloquesNA, empresa, trabajadores }); await completarFormularioPublico(token); setCompletado(true); window.scrollTo(0, 0); }
    catch { toast.error("No se pudo finalizar el formulario"); }
  };

  const fotosDe = (orden) => (data.formulario.fotos || []).filter((f) => f.bloque_orden === orden);

  if (completado) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md text-center" data-testid="form-completado">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-emerald-600" /></div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">¡Formulario enviado!</h1>
          <p className="text-sm text-slate-500">Gracias por facilitar la información. El técnico la revisará antes de la visita. Puede cerrar esta ventana.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Cabecera + progreso */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-5 h-5 text-blue-700" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{data.formulario.nombre}</p>
              <p className="text-xs text-slate-400 truncate">{data.contexto.empresa}{data.contexto.centro ? ` · ${data.contexto.centro}` : ""}</p>
            </div>
            <span className="ml-auto text-xs text-slate-400 flex items-center gap-1">{saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Guardando</> : "Guardado"}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} data-testid="form-progreso-bar" />
            </div>
            <span className="text-xs font-semibold text-slate-600 whitespace-nowrap" data-testid="form-progreso">{pct}%</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        <p className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
          Indique el estado de cada punto en su centro: <b>Cumple</b> (correcto), <b>No cumple</b> (existe una deficiencia), <b>No aplica</b> (no procede en su actividad) o <b>No sé</b> (no sabe la respuesta). En las preguntas <b>informativas</b> las opciones serán <b>Sí / No</b>. Puede añadir observaciones y fotos, y <b>no es necesario responderlo todo</b>: puede enviar el formulario aunque quede incompleto. El progreso se guarda automáticamente.
        </p>
        {data.pide_datos_empresa && (
          <>
            <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="form-empresa">
              <div className="flex items-center gap-2 mb-3"><Building2 className="w-4 h-4 text-blue-700" /><p className="font-medium text-slate-800 text-sm">Datos de la empresa</p></div>
              <div className="grid sm:grid-cols-2 gap-3">
                {EMP_FIELDS.map(([k, lbl]) => (
                  <label key={k} className="block">
                    <span className="text-xs text-slate-500">{lbl}</span>
                    <input type="text" defaultValue={empresa[k] || ""} onBlur={(e) => setEmp(k, e.target.value)} data-testid={`form-emp-${k}`}
                      className="w-full mt-1 h-9 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-400" />
                  </label>
                ))}
              </div>
            </section>
            <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="form-trabajadores">
              <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-blue-700" /><p className="font-medium text-slate-800 text-sm">Datos de los trabajadores</p></div>
              {trabajadores.length === 0 && <p className="text-xs text-slate-400 mb-3">Añada los trabajadores del centro.</p>}
              <div className="space-y-3">
                {trabajadores.map((t) => (
                  <div key={t.id} className="grid sm:grid-cols-6 gap-2 items-center border-b border-slate-50 pb-3 last:border-0" data-testid={`form-trab-${t.id}`}>
                    <input placeholder="Nombre" defaultValue={t.nombre} onBlur={(e) => setTrab(t.id, "nombre", e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-400" />
                    <input placeholder="Apellidos" defaultValue={t.apellidos} onBlur={(e) => setTrab(t.id, "apellidos", e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-400" />
                    <input placeholder="DNI" defaultValue={t.dni} onBlur={(e) => setTrab(t.id, "dni", e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-400" />
                    <input placeholder="Puesto" defaultValue={t.puesto} onBlur={(e) => setTrab(t.id, "puesto", e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-400" />
                    <input placeholder="Tareas que realiza" defaultValue={t.tareas} onBlur={(e) => setTrab(t.id, "tareas", e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-400" />
                    <button onClick={() => delTrab(t.id)} data-testid={`form-trab-del-${t.id}`} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600 justify-self-start"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <button onClick={addTrab} data-testid="form-trab-add" className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-100"><Plus className="w-4 h-4" /> Añadir trabajador</button>
            </section>
          </>
        )}
        {bloques.map((b) => {
          const na = bloquesNA.includes(b.orden);
          const open = !!abiertos[b.orden];
          const respB = b.preguntas.filter((q) => respuestas[q.checklist_id]?.respuesta).length;
          return (
            <section key={b.orden} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`form-bloque-${b.orden}`}>
              <button onClick={() => setAbiertos((a) => ({ ...a, [b.orden]: !open }))} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`form-bloque-toggle-${b.orden}`}>
                <span className="w-7 h-7 rounded-lg bg-blue-50 text-blue-700 text-sm font-semibold flex items-center justify-center shrink-0">{b.orden}</span>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium text-slate-800 text-sm">{b.titulo}</span>
                  <span className="block text-xs text-slate-400">{na ? "No aplica a mi actividad" : `${respB}/${b.preguntas.length} respondidas`}</span>
                </span>
                {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>
              {open && (
                <div className="px-4 pb-4 border-t border-slate-100">
                  {b.ayuda && <p className="text-xs text-slate-400 mt-3">{b.ayuda}</p>}
                  {b.opcional && (
                    <label className="flex items-center gap-2 mt-3 text-sm text-slate-600 cursor-pointer" data-testid={`form-bloque-na-${b.orden}`}>
                      <input type="checkbox" checked={na} onChange={() => toggleNA(b.orden)} className="w-4 h-4 rounded" />
                      Este bloque no aplica a mi actividad
                    </label>
                  )}
                  {!na && (
                    <div className="mt-3 space-y-4">
                      {b.preguntas.map((q) => {
                        const r = respuestas[q.checklist_id] || {};
                        const opts = q.tipo === "info" ? RESP_INFO : RESP;
                        return (
                          <div key={q.checklist_id} className="border-b border-slate-50 pb-3 last:border-0" data-testid={`form-preg-${q.checklist_id}`}>
                            <p className="text-sm text-slate-700 mb-2">{q.pregunta}</p>
                            <div className="flex flex-wrap gap-2">
                              {opts.map((o) => (
                                <button key={o.v} onClick={() => setResp(q.checklist_id, { respuesta: o.v })}
                                  data-testid={`form-resp-${q.checklist_id}-${o.v}`}
                                  className={`flex-1 min-w-[68px] h-9 rounded-lg text-sm font-medium border transition-colors ${r.respuesta === o.v ? o.on : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                                  {o.l}
                                </button>
                              ))}
                            </div>
                            {r.respuesta && (
                              <input type="text" defaultValue={r.observacion || ""} onBlur={(e) => setResp(q.checklist_id, { observacion: e.target.value })}
                                placeholder="Observaciones (opcional)" data-testid={`form-obs-${q.checklist_id}`}
                                className="w-full mt-2 h-9 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-400" />
                            )}
                          </div>
                        );
                      })}
                      {/* Fotos del bloque */}
                      <div>
                        <p className="text-xs text-slate-500 mb-2">Fotografías (opcional)</p>
                        <div className="flex flex-wrap gap-2">
                          {fotosDe(b.orden).map((f) => (
                            <div key={f.id} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                              <img src={fotoUrl(f.url)} alt="" className="w-full h-full object-cover" />
                              <button onClick={() => delFoto(f.id)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                            </div>
                          ))}
                          <label className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 cursor-pointer hover:border-blue-400" data-testid={`form-foto-${b.orden}`}>
                            <Camera className="w-5 h-5" />
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) onFoto(b.orden, e.target.files[0]); e.target.value = ""; }} />
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}

        {/* Vídeo opcional */}
        <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="form-video">
          <div className="flex items-center gap-2 mb-2"><Video className="w-4 h-4 text-blue-700" /><p className="font-medium text-slate-800 text-sm">Vídeo del centro (opcional)</p></div>
          <p className="text-xs text-slate-400 mb-3">Puede grabar un recorrido del centro. Formatos MP4 o MOV, máx. {data.contexto.video_max_mb} MB.</p>
          {data.formulario.video ? (
            <div className="flex items-center gap-3 text-sm text-slate-600">
              <Check className="w-4 h-4 text-emerald-600" /> Vídeo subido
              <button onClick={delVideo} className="ml-auto text-red-600 text-xs">Eliminar</button>
            </div>
          ) : videoPct !== null ? (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Subiendo… {videoPct}%</div>
          ) : (
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 text-sm text-slate-600 cursor-pointer hover:border-blue-400" data-testid="form-video-input">
              <Video className="w-4 h-4" /> Subir vídeo
              <input type="file" accept="video/mp4,video/quicktime" className="hidden" onChange={(e) => { if (e.target.files[0]) onVideo(e.target.files[0]); e.target.value = ""; }} />
            </label>
          )}
        </section>
      </main>

      {/* Barra inferior fija */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-20">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <span className="text-sm text-slate-500">{pct}% completado</span>
          <button onClick={finalizar} data-testid="form-finalizar" className="ml-auto inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-blue-700 text-white font-medium hover:bg-blue-800">
            <Send className="w-4 h-4" /> Enviar formulario
          </button>
        </div>
      </div>
    </div>
  );
}
